import React from "react";

import _ from "lodash";

import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { ObjectPropertyType } from "commons/types/validation";

import { notEmptyValidator } from "commons/utils/validate";

import { useBlockGroupContext } from "components/form/Blocks";
import { AttachmentType } from "components/ui/AttachmentPicker";

import { useSaveActionsContext } from "context/save";

import useSectionFiveImagesFifteenTextsApi from "hooks/api/private/blocks/useSectionFiveImagesFifteenTextsApi";
import useMobileOrDesktopFile from "hooks/useMobileOrDesktopFile";
import { ButtonDeleteInterface } from "hooks/useTitleFunctionsTopBlock";

import {
    SectionFiveImagesFifteenTextsBody,
    SectionFiveImagesFifteenTextsDetails,
} from "types/api/blocks/sectionFiveImagesFifteenTexts";
import { DesktopMobileForm, LanguageEnum } from "types/api/commons";
import { DesktopOrMobile } from "types/enums";

import { converterAttachmentToFileWithPayload, createAttachmentType } from "utils/utils";
import { getExtraErrorsDescription, possibleEmptyMedia } from "utils/validators";

export type SectionOneImageFiveTextsForm = {
    id: string | undefined;
    img_id: string | null;
    img: DesktopMobileForm | null;
    text: {
        [x in LanguageEnum]: {
            text_3: string;
            text_1: string;
            text_5: string;
            text_6: string;
            text_4: string;
            text_2: string;
            text_7: string;
            text_8: string;
            text_9: string;
        };
    };
};

type SectionOneImageFiveTextsType = {
    setImage: (fileToSet: AttachmentType, target: DesktopOrMobile) => void;
    buttonDelete: ButtonDeleteInterface;
    formHook: FormHookType<SectionOneImageFiveTextsForm>;
};

function useSectionOneImageNineTexts({
    allOptional = false,
}: {
    allOptional?: boolean;
}): SectionOneImageFiveTextsType {
    const { blockGroupCompositionId, blockId, graphicalOrder, blockGroupId } =
        useBlockGroupContext();

    const {
        saveSectionFiveImagesFifteenTexts,
        getOneSectionFiveImagesFifteenTexts,
        deleteSectionFiveImagesFifteenTexts,
    } = useSectionFiveImagesFifteenTextsApi();

    const schema = React.useMemo<ObjectPropertyType>(
        () => ({
            type: "object",
            properties: {
                id: {
                    type: "simple",
                    validators: [],
                },
                img_id: {
                    type: "simple",
                    validators: [],
                },
                img: possibleEmptyMedia,
                text: {
                    type: "collection",
                    of: {
                        type: "object",
                        properties: {
                            text_3: {
                                type: "simple",
                                validators: allOptional ? [] : [notEmptyValidator],
                            },
                            text_4: {
                                type: "simple",
                                validators: allOptional ? [] : [notEmptyValidator],
                            },
                            text_1: {
                                type: "simple",
                                validators: [],
                            },
                            text_2: {
                                type: "simple",
                                validators: allOptional ? [] : [notEmptyValidator],
                            },
                            text_5: {
                                type: "simple",
                                validators: [],
                            },
                            text_6: {
                                type: "simple",
                                validators: [],
                            },
                            text_7: {
                                type: "simple",
                                validators: [],
                            },
                            text_8: {
                                type: "simple",
                                validators: [],
                            },
                            text_9: {
                                type: "simple",
                                validators: [],
                            },
                        },
                    },
                },
            },
        }),
        [allOptional],
    );

    const initialValues: SectionOneImageFiveTextsForm = React.useMemo(() => {
        return {
            id: undefined,
            img_id: null,
            img: {
                id: undefined,
                desktop: createAttachmentType({ id: null, label: "Desktop" }),
                mobile: createAttachmentType({ id: null, label: "Mobile" }),
            },
            text: {
                it: {
                    text_3: "",
                    text_1: "",
                    text_5: "",
                    text_6: "",
                    text_4: "",
                    text_2: "",
                    text_7: "",
                    text_8: "",
                    text_9: "",
                },
                en: {
                    text_3: "",
                    text_1: "",
                    text_5: "",
                    text_6: "",
                    text_4: "",
                    text_2: "",
                    text_7: "",
                    text_8: "",
                    text_9: "",
                },
            },
        };
    }, []);

    const formHook = useForm<SectionOneImageFiveTextsForm>(
        schema,
        initialValues,
        getExtraErrorsDescription,
    );

    const { showAllError, setForm, isValid, form } = formHook;

    const { setDesktopMobileImage: setImage } = useMobileOrDesktopFile({
        setForm: setForm,
        path: "img",
    });

    const [sectionDetails, setSectionDetails] = React.useState<
        SectionFiveImagesFifteenTextsDetails | undefined
    >(undefined);

    React.useEffect(() => {
        const [apiCall, abortController] = getOneSectionFiveImagesFifteenTexts(
            { id: blockGroupCompositionId },
            { rejectNotFound: true },
        );

        apiCall.then(setSectionDetails).catch((error) => console.log(error));

        return () => abortController.abort();
    }, [setSectionDetails, getOneSectionFiveImagesFifteenTexts, blockGroupCompositionId]);

    React.useEffect(() => {
        if (sectionDetails) {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.id = sectionDetails.id;

                newForm.text = sectionDetails.text;

                newForm.img_id = sectionDetails.img_one_id;

                if (sectionDetails.img_one) {
                    newForm.img = {
                        id: sectionDetails.img_one.id,
                        desktop: createAttachmentType({
                            id: sectionDetails.img_one.desktop.id,
                            label: "Desktop",
                            filename: sectionDetails.img_one.desktop.filename,
                            status: 2,
                        }),
                        mobile: createAttachmentType({
                            id: sectionDetails.img_one.mobile.id,
                            label: "Mobile",
                            filename: sectionDetails.img_one.mobile.filename,
                            status: 2,
                        }),
                    };
                } else {
                    newForm.img = {
                        id: undefined,
                        desktop: createAttachmentType({ id: null, label: "Desktop" }),
                        mobile: createAttachmentType({ id: null, label: "Mobile" }),
                    };
                }

                return newForm;
            });
        }
    }, [setForm, sectionDetails]);

    const { addSaveAction } = useSaveActionsContext();

    React.useEffect(() => {
        addSaveAction(blockGroupCompositionId, () => {
            if (isValid) {
                console.log(form);

                const sectionBody: SectionFiveImagesFifteenTextsBody = {
                    id: blockGroupCompositionId,
                    text: {
                        it: {
                            text_3: form.text.it.text_3,
                            text_4: form.text.it.text_4,
                            text_1: form.text.it.text_1,
                            text_2: form.text.it.text_2,
                            text_5: form.text.it.text_5,
                            text_6: form.text.it.text_6,
                            text_7: form.text.it.text_7,
                            text_8: form.text.it.text_8,
                            text_9: form.text.it.text_9,
                            text_10: "",
                            text_11: "",
                            text_12: "",
                            text_13: "",
                            text_14: "",
                            text_15: "",
                        },
                        en: {
                            text_3: form.text.en.text_3,
                            text_4: form.text.en.text_4,
                            text_1: form.text.en.text_1,
                            text_2: form.text.en.text_2,
                            text_5: form.text.en.text_5,
                            text_6: form.text.en.text_6,
                            text_7: form.text.en.text_7,
                            text_8: form.text.en.text_8,
                            text_9: form.text.en.text_9,
                            text_10: "",
                            text_11: "",
                            text_12: "",
                            text_13: "",
                            text_14: "",
                            text_15: "",
                        },
                    },
                    img_one: {},
                    img_two: {},
                    img_three: {},
                    img_four: {},
                    img_five: {},
                };

                let addImageOne = false;

                if (form.img && form.img.desktop && form.img.mobile) {
                    if (form.img.desktop.filename && form.img.mobile.filename) {
                        sectionBody.img_one = {
                            id: form.img?.id,
                            desktop: form.img
                                ? converterAttachmentToFileWithPayload(form.img.desktop)
                                : undefined,
                            mobile: form.img
                                ? converterAttachmentToFileWithPayload(form.img.mobile)
                                : undefined,
                        };

                        addImageOne = true;
                    }
                }

                if (addImageOne === false) {
                    sectionBody.img_one_id = null;
                }

                const [apiCall] = saveSectionFiveImagesFifteenTexts(sectionBody);

                const promises: Promise<unknown>[] = [];

                promises.push(apiCall);

                return Promise.all(promises);
            } else {
                showAllError();
                return Promise.reject("INVALID_FORM");
            }
        });
    }, [
        saveSectionFiveImagesFifteenTexts,
        addSaveAction,
        showAllError,
        blockGroupCompositionId,
        isValid,
        form,
        blockGroupId,
        blockId,
        graphicalOrder,
    ]);

    return React.useMemo(
        (): SectionOneImageFiveTextsType => ({
            buttonDelete: deleteSectionFiveImagesFifteenTexts,
            setImage,
            formHook,
        }),
        [deleteSectionFiveImagesFifteenTexts, setImage, formHook],
    );
}

export default useSectionOneImageNineTexts;
